import ApiService from "@/core/services/api.service";
import JwtService from "@/core/services/jwt.service";
import router from "@/router";
import i18n from "@/core/plugins/vue-i18n";

// action types
export const VERIFY_AUTH = "verifyAuth";
export const VERIFY_LOGIN = "verifyLogin";
export const LOGIN = "login";
export const LOGIN_SSO = "loginSSO";
export const LOGOUT = "logout";
export const LOGOUT_NO_REDIRECT = "logoutNoRedirect";
export const REGISTER = "register";
export const UPDATE_PASSWORD = "updateUser";
export const UPDATE_ON_BOARDING = "updateOnBoarding";

// mutation types
export const PURGE_AUTH = "purgeAuth";
export const SET_AUTH = "setUser";
export const SET_AUTH_EMAIL = "setAuthEmail";
export const SET_PASSWORD = "setPassword";
export const SET_ERROR = "setError";
export const SET_ON_BOARDING = "setOnBoarding";

const state = {
  errors: null,
  user: {},
  currentAuthEmail : null,
  isAuthenticated: JwtService.getToken() !== 'undefined',
  onBoarding: true
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  currentAuthEmail(state) {
    return state.currentAuthEmail;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  // TODO: Association des connexions si connecté ou pas et page selon role *************
  isFreemium(state) {
    return typeof state.user.roles !== 'undefined' && state.user.roles.includes('ROLE_FREEMIUM');
  },
  isFreemiumAuthorized(state) {
    return typeof state.user.roles !== 'undefined' && state.user.roles.includes('ROLE_FREEMIUM') && state.user.freemiumIsAuthorized;
  },
  isSuperUser(state) {
    return typeof state.user.roles !== 'undefined' && (state.user.roles.includes('ROLE_SUPER_USER') /*|| state.user.roles.includes('ROLE_ADMIN')*/);
  },
  isAdmin(state) {
    return typeof state.user.roles !== 'undefined' && state.user.roles.includes('ROLE_ADMIN');
  },
  onBoarding(state) {
    return state.onBoarding;
  }
};

const actions = {
  [VERIFY_AUTH](context) {
    return new Promise(resolve => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("users", "me")
          .then(({ data }) => {
            context.commit(SET_AUTH, {
              user: data,
              token: JwtService.getToken()
            });
            resolve();
          })
          .catch(() => {
            // Token expiré
            if (router.currentRoute.name !== 'login') {
              router.push({ name: 'login' });
            }
            context.commit(PURGE_AUTH);
            resolve();
          });
      } else {
        context.commit(PURGE_AUTH);
        resolve();
      }
    });
  },
  [VERIFY_LOGIN](context, credentials){
    return new Promise((resolve, reject) => {
      localStorage.setItem('auth_email', credentials.email);
      localStorage.setItem('auth_client', undefined);
      ApiService.authenticate("verify_login", credentials)
        .then(({ data }) => {
          if(data.client != undefined){
            localStorage.setItem('auth_client', data.client);
          }
          resolve(data);
        })
        .catch((response) => {
          console.log(response);
          if(response && response.status){
            switch (response.status) {
              case 401:
                context.commit(SET_ERROR, [{ message: i18n.t('login.invalidCredentials') }]);
                break;
              default:
                context.commit(SET_ERROR, [response.data]);
            }
          }
          else{
            context.commit(SET_ERROR, [{ message: i18n.t('login.network_error') }]);
          }
          reject(response);
        });
    });
  },
  [LOGIN](context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.authenticate("authentication_token", credentials)
        .then(({ data }) => {
          context.commit(SET_AUTH, data);
          resolve(data);
        })
        .catch(({ response }) => {
          switch (response.status) {
            case 401:
              context.commit(SET_ERROR, [{ message: i18n.t('login.invalidCredentials') }]);
              break;
            default:
              context.commit(SET_ERROR, [response.data]);
          }
          reject(response);
        });
    });
  },
  [LOGIN_SSO](context, params) {
    return new Promise((resolve, reject) => {
      ApiService.authenticate_sso("login/sso", params)
        .then(({ data }) => {
          context.commit(SET_AUTH, data);
          resolve(data);
        })
        .catch(({response}) => {
          localStorage.setItem('auth_errors', undefined);
          switch (response.status) {
            case 401:
              localStorage.setItem('auth_errors', JSON.stringify([{ message: i18n.t('login.invalidCredentials') }]));
              break;
            default:
              localStorage.setItem('auth_errors', JSON.stringify([response.data]));
          }
          reject(response);
        });
    });
  },
  [LOGOUT_NO_REDIRECT](context) {
    context.commit(PURGE_AUTH);
  },
  [LOGOUT]() {
    return new Promise(resolve => {
      ApiService.post("logout", {}, { baseURL: process.env.VUE_APP_API })
        .then(({data}) =>  data && data['signout_url'] ? resolve(data['signout_url']) : resolve(null))
        .catch(() => resolve(null) );
    });
  },
  [REGISTER](context, credentials) {
    return new Promise(resolve => {
      ApiService.post("login", credentials)
        .then(({ data }) => {
          context.commit(SET_AUTH, data);
          resolve(data);
        })
        .catch(({ response }) => {
          context.commit(SET_ERROR, response.data.errors);
        });
    });
  },
  [UPDATE_PASSWORD](context, payload) {
    return ApiService.put("password", payload).then(({ data }) => {
      context.commit(SET_PASSWORD, data);
      return data;
    });
  },
  [UPDATE_ON_BOARDING](context, payload) {
    context.commit(SET_ON_BOARDING, payload);
  }
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_AUTH](state, data) {
    state.isAuthenticated = true;
    state.user = {
      ...state.user,
      ...data.user,
    };
    state.errors = {};
    JwtService.saveToken(data.token);
  },
  [SET_AUTH_EMAIL](state, email) {
    state.currentAuthEmail = email;
  },
  [SET_PASSWORD](state, password) {
    state.user.password = password;
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.user = {};
    state.errors = {};
    JwtService.destroyToken();
  },
  [SET_ON_BOARDING](state, value) {
    state.onBoarding = value;
  }
};

export default {
  state,
  actions,
  mutations,
  getters
};
